import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, CardHeader, List, ListItem, ListItemButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import withObservables from '@nozbe/with-observables'
import _ from 'lodash'
import AddDeviceButton from './add-device-modal';
import { useNavigate } from 'react-router-dom';
import DeviceListQueryBuilder, { observeDevicesListFromQuery, useQueryBuilder } from './device-list-query-builder';
import LabelsCsvExportButton from './labels-csv-export-button';
import dayjs from 'dayjs';
import { withRegistrationDatabase } from '../../../../model/registration/RegistrationDatabase';
import SelectDeviceWithScanner from './select-with-scanner';

export const useNavigateToDevice = () => {
    const navigate = useNavigate();
    const handleClick = useCallback((id) => {
        navigate(`${id}/details`);
    }, []);
    return handleClick;
}

const DeviceField = ({ label, value, width }) => (
    <Box sx={{ width, minWidth: width, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" noWrap>{value || '-'}</Typography>
    </Box>
);

DeviceField.propTypes = {
    label: PropTypes.string,
    value: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export function FactoryDeviceListItem({ device, hardwareVersion, firmware, index }) {

    const handleClick = useNavigateToDevice();

    return (
        <ListItem disablePadding onClick={() => handleClick(device.id)}>
            <ListItemButton>
                <Box sx={{ display: 'flex', gap: 2, width: '100%', alignItems: 'flex-start' }}>
                    <DeviceField label="#" value={String(index + 1)} width={30} />
                    <DeviceField label="ID" value={device.id} width={202} />
                    <DeviceField label="Hardware" value={device.hardware} width={130} />
                    <DeviceField label="Status" value={device.factoryStatus} width={90} />
                    <DeviceField label="HW Version" value={hardwareVersion?.version} width={100} />
                    <DeviceField label="Firmware" value={firmware?.version} width={100} />
                    <DeviceField label="Created" value={dayjs(device.createdAt).format('YYYY-MM-DD HH:mm')} width={120} />
                    <DeviceField label="Updated" value={dayjs(device.updatedAt).format('YYYY-MM-DD HH:mm')} width={120} />
                </Box>
            </ListItemButton>
        </ListItem>
    );
}

FactoryDeviceListItem.propTypes = {
    device: PropTypes.any,
    hardwareVersion: PropTypes.any,
    firmware: PropTypes.any,
    index: PropTypes.number
}

const observeDevice = withObservables(['device'], ({ device }) => {
    return ({
        device,
        hardwareVersion: device.hardwareVersion,
        firmware: device.firmware,
    })
});

export const ObservedFactoryDeviceListItem = withRegistrationDatabase(observeDevice(FactoryDeviceListItem));

export function FactoryDevicesListInternal({ devices }) {
    return (
        <List dense>
            {_.map(devices, (device, index) => <ObservedFactoryDeviceListItem key={device.id} device={device} index={index} />)}
        </List>
    );
}

FactoryDevicesListInternal.propTypes = {
    devices: PropTypes.array
}

export const ObservedFactoryDevicesList = observeDevicesListFromQuery(FactoryDevicesListInternal)

export default function FactoryDevicesList({ }) {

    const [query, setQuery] = useQueryBuilder();
    const handleClick = useNavigateToDevice();

    return (
        <Card>
            <CardHeader
                action={(
                    <Stack spacing={1} direction='row' alignItems='center'>
                        <SelectDeviceWithScanner onClick={handleClick} />
                        <DeviceListQueryBuilder query={query} setQuery={setQuery} />
                        <LabelsCsvExportButton query={query} />
                        <AddDeviceButton />
                    </Stack>
                )}
                title="Devices"
            />
            <CardContent>
                <ToggleButtonGroup
                    value={query.sortBy}
                    exclusive
                    onChange={(e, value) => value && setQuery({ ...query, sortBy: value })}
                    size="small"
                >
                    <ToggleButton value="created_at">New first</ToggleButton>
                    <ToggleButton value="updated_at">Last updated first</ToggleButton>
                </ToggleButtonGroup>
            </CardContent>
            <CardContent>
                <ObservedFactoryDevicesList query={query} />
            </CardContent>
        </Card>
    );
};

FactoryDevicesList.propTypes = {
}
